import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IZoomDAO, IZoomMeetingDTO } from '../../../../api/zoom.dao';
import { IRouter } from '../../../../infrastructure/router';
import { RouteParams, Routes } from '../../../../routes-table';
import { IErrorParsing } from '../../../../infrastructure/error-parsing';
import { MeetingFieldsViewModel } from '../shared/meeting-form-fields/meeting-form-fields.viewmodel';
import { IAlertsService } from '../../../../infrastructure/alerts';
import { alertServerError } from '../../../global/modal/alerts-host/alert-templates';

class UpdateMeetingViewModel {
    @mobx.observable
    private loading: boolean = false;
    @mobx.observable
    private meeting: IZoomMeetingDTO;
    @mobx.observable
    private error : string;

    private zoomMeetingId: string;
    private formFields: MeetingFieldsViewModel;

    constructor(
        public meetingId: number,
        private readonly zoomDao: IZoomDAO = IoC.Container.get(IZoomDAO),
        private readonly router: IRouter<RouteParams> = IoC.Container.get(IRouter),
        private readonly errorParsing: IErrorParsing = IoC.Container.get(IErrorParsing),
        private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
    ) {
        this.formFields = new MeetingFieldsViewModel();
    }

    public getFormFields() {
        return this.formFields;
    }

    public getIsLoading = () => {
        return this.loading;
    };

    public getMeetingId = () => {
        return this.meetingId;
    };

    public getZoomMeetingId = () => {
        return this.zoomMeetingId;
    };

    @mobx.action
    public fetchMeetingDetails = async () => {
        this.loading = true;

        try {
            const meetingDetails = await this.zoomDao.getScheduledMeetingDetails(this.meetingId);

            mobx.runInAction(() => {
                this.zoomMeetingId = meetingDetails.zoomId;
                this.formFields = new MeetingFieldsViewModel(meetingDetails);
                this.formFields.isEditMode = true;
            });
        } catch (e) {
            mobx.runInAction(() => {
                this.error = this.errorParsing.extractAPIError(e).errorMessage;
                throw e;
            });
        } finally {
            mobx.runInAction(() => {
                this.loading = false;
            });
        }
    };

    @mobx.action
    public updateMeeting = async () => {
        this.loading = true;

        try {
            this.meeting = {
                startTime: this.formFields.startDateTime,
                endTime: this.formFields.endDateTime,
                timeZone: this.formFields.timeZone,
                topic: this.formFields.topic,
                agenda: this.formFields.description,
            };

            await this.zoomDao.updateMeeting(this.meetingId, this.meeting);
            this.router.navigate(Routes.ZoomScheduledMeetingDetails, { meetingId: this.meetingId.toString() });
        } catch (e) {
            alertServerError('Error', this.errorParsing.extractMessage(e));
        } finally {
            mobx.runInAction(() => {
                this.loading = false;
            });
        }
    };

    @mobx.action
    public submitForm = async () => {
        if (!this.formFields.preSubmitValidation()) {
            return;
        }

        this.formFields.hideErrorMessages();
        await this.updateMeeting();
    };
}

export { UpdateMeetingViewModel };
